import { jsx, jsxs } from "react/jsx-runtime";
import { Add } from "@wix/wix-ui-icons-common";
import { Button, PaddingModes, HoverStyles } from "@wix/wix-ui-tpa/cssVars";
import React__default, { useRef, useEffect } from "react";
import { useFocusFieldEvent } from "../../../../contexts/events-context.js";
import { useId } from "../../../../hooks/use-id.js";
import { st, classes } from "./FileUpload.st.css";
const FileUpload = ({
  fieldId,
  inputId,
  fileInputRef,
  fileUploadButtonRef,
  textBelowButtonRef,
  accept,
  files,
  multiple = false,
  buttonText,
  onChange,
  onFocus,
  onBlur,
  className,
  disabled,
  ariaRoleDescription,
  ariaDescribedBy,
  ariaLabelledBy,
  required
}) => {
  const [checkErrorFocus, setCheckErrorFocus] = React__default.useState(false);
  const dialogOpenRef = React__default.useRef(false);
  const fileHasError = Boolean(files?.find((file) => file.state === "error"));
  const filesExist = files && Boolean(files.length);
  const textBellowButton = textBelowButtonRef.current;
  const buttonTextId = useId();
  const buttonTextRef = useRef(null);
  useEffect(() => {
    const listener = () => {
      dialogOpenRef.current = false;
      handleFileChange(new FileList());
    };
    const inputEl = fileInputRef.current;
    if (inputEl) {
      inputEl.addEventListener("cancel", listener);
    }
    return () => {
      inputEl?.removeEventListener("cancel", listener);
    };
  }, [fileInputRef.current]);
  useEffect(() => {
    if (buttonTextRef.current) {
      buttonTextRef.current.id = buttonTextId;
    }
  }, [buttonTextId]);
  const ariaLabelledByIds = [ariaLabelledBy, buttonTextId].filter(Boolean).join(" ");
  const handleFileChange = (_files) => {
    textBelowButtonRef.current = null;
    onChange?.(_files);
    dialogOpenRef.current = false;
    setCheckErrorFocus(true);
  };
  const clickHandler = React__default.useCallback(() => {
    dialogOpenRef.current = true;
    fileInputRef.current?.click();
  }, [fileInputRef]);
  React__default.useEffect(() => {
    if (checkErrorFocus && filesExist && fileHasError && textBellowButton) {
      textBellowButton.focus();
      setCheckErrorFocus(false);
    }
  }, [checkErrorFocus, filesExist, fileHasError, textBellowButton]);
  useFocusFieldEvent(() => {
    fileUploadButtonRef.current?.focus({ preventScroll: true });
  }, fieldId);
  const handleInputChange = (e) => {
    handleFileChange(e.currentTarget.files);
    e.target.value = "";
  };
  function handleBlur(e) {
    if (dialogOpenRef.current) {
      dialogOpenRef.current = false;
      return;
    }
    onBlur?.(e);
  }
  return /* @__PURE__ */ jsx("div", { className: st(classes.root, className), "data-hook": "file-upload-root", children: /* @__PURE__ */ jsxs("div", { className: classes.mainContainer, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        className: classes.input,
        ref: fileInputRef,
        type: "file",
        accept,
        onChange: handleInputChange,
        multiple
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        hoverStyle: HoverStyles.underline,
        className: classes.button,
        id: inputId,
        ref: fileUploadButtonRef,
        contentRef: buttonTextRef,
        contentClassName: classes.buttonText,
        paddingMode: PaddingModes.explicitPaddings,
        onClick: clickHandler,
        onFocus,
        onBlur: handleBlur,
        disabled,
        prefixIcon: /* @__PURE__ */ jsx(Add, { "aria-hidden": "true" }),
        "aria-roledescription": ariaRoleDescription,
        "aria-labelledby": ariaLabelledByIds,
        "aria-describedby": ariaDescribedBy,
        "aria-required": required,
        children: buttonText
      }
    )
  ] }) });
};
FileUpload.displayName = "FileUpload";
export {
  FileUpload
};
//# sourceMappingURL=FileUpload.js.map
